/* eslint-disable @typescript-eslint/no-require-imports */
import scrollbarHide from 'tailwind-scrollbar-hide';
import type { Config } from 'tailwindcss';

import { hexToHsl, hslToString } from './theme/colorUtilities';

const defValue = (value?: string) => (value ? `, ${value.startsWith('#') ? hslToString(hexToHsl(value)) : value}` : '');
const variable = (name: string, defaultValue = '') => `var(--cb-${name}${defValue(defaultValue)})`;
const color = (name: string, defaultValue = '') => `hsl(${variable(name, defaultValue)} / <alpha-value>)`;

const config: Config = {
  content: ['./components/**/*.{js,ts,jsx,tsx,mdx}', './theme/**/*.{js,ts,jsx,tsx,mdx}'],

  theme: {
    data: {
      closed: 'closed',
    },
    extend: {
      screens: {
        mobile: '600px',
        tablet: '1024px',
        desktop: '1440px',
      },
      maxWidth: {
        desktop: '1140px',
        tablet: '1024px',
        mobile: '600px',
      },
      fontSize: {
        'header-mobile': [
          '2.5rem',
          {
            lineHeight: '2.5rem',
            letterSpacing: '-0.02rem',
          },
        ],
        'header-large': [
          '4.5rem',
          {
            lineHeight: '4.5rem',
            letterSpacing: '-0.02rem',
          },
        ],
        'header-medium': [
          '3rem',
          {
            lineHeight: '3rem',
            letterSpacing: '-0.02em',
          },
        ],
        'header-small': [
          '1.875rem',
          {
            lineHeight: '2rem',
            letterSpacing: '-0.02em',
          },
        ],
        'header-card': [
          '1.25rem',
          {
            lineHeight: '1.75rem',
            letterSpacing: '-0.02em',
          },
        ],
        lead: [
          '1.5rem',
          {
            lineHeight: '2rem',
            fontWeight: '400',
          },
        ],
        'lead-mobile': [
          '1.125rem',
          {
            lineHeight: '1.5rem',
            fontWeight: '400',
          },
        ],
        label: [
          '0.875rem',
          {
            lineHeight: '1.25rem',
            fontWeight: '600',
          },
        ],
        paragraph: [
          '1.125rem',
          {
            lineHeight: '1.75rem',
            fontWeight: '400',
          },
        ],
        caption: [
          '0.875rem',
          {
            lineHeight: '1.25rem',
            fontWeight: '600',
          },
        ],
        button: [
          '1rem',
          {
            lineHeight: '1.5rem',
            fontWeight: '700',
          },
        ],
      },
      fontFamily: {
        header: [variable('font-header', 'Outfit'), 'system-ui, -apple-system, sans-serif'],
        body: [variable('font-body', 'Manrope'), 'system-ui, -apple-system, sans-serif'],
      },
      colors: ({ colors }) => ({
        /**
         * New Tailwind theme
         */
        primary: {
          50: color('color-primary-50', '219deg 100% 97%'),
          100: color('color-primary-100', '221deg 100% 94%'),
          200: color('color-primary-200', '220deg 100% 87%'),
          300: color('color-primary-300', '220deg 100% 79%'),
          400: color('color-primary-400', '221deg 100% 72%'),
          500: color('color-primary-500', '220deg 100% 65%'),
          600: color('color-primary-600', '221deg 81% 56%'),
          700: color('color-primary-700', '221deg 65% 46%'),
          800: color('color-primary-800', '221deg 65% 36%'),
          900: color('color-primary-900', '220deg 66% 27%'),
          950: color('color-primary-950', '219deg 64% 18%'),
          DEFAULT: color('color-primary', variable('color-primary-600', '221deg 81% 56%')),
          aa: color('color-primary-aa', '219deg 99% 59%'),
        },

        secondary: {
          100: color('color-secondary-100', '82deg 100% 92%'),
          200: color('color-secondary-200', '80deg 100% 82%'),
          300: color('color-secondary-300', '77deg 100% 71%'),
          400: color('color-secondary-400', '72deg 100% 43%'),
          500: color('color-secondary-500', '72deg 100% 43%'),
          600: color('color-secondary-600', '72deg 100% 27%'),
          700: color('color-secondary-700', '73deg 100% 21%'),
          800: color('color-secondary-800', '73deg 100% 14%'),
          900: color('color-secondary-900', '86deg 68% 19%'),
          950: color('color-secondary-950', '78deg 100% 5%'),
          DEFAULT: color('color-secondary', variable('color-secondary-300', '77deg 100% 71%')),
        },

        content: {
          primary: color('color-content-primary', colors.slate[950]),
          secondary: color('color-content-secondary', colors.slate[700]),
          tertiary: color('color-content-tertiary', colors.slate[500]),
          inverse: color('color-content-inverse', colors.white),
          highlight: color('color-content-highlight', variable('color-secondary-300', '77deg 100% 71%')),
        },

        background: {
          /**
           * New Tailwind theme
           */
          primary: color('color-background-primary', colors.white),
          secondary: color('color-background-secondary', colors.slate[50]),
          tertiary: color('color-background-tertiary', colors.slate[100]),
          hover: color('color-background-hover', variable('color-primary-500', '220deg 100% 65%')),
          inverse: {
            primary: color('color-background-inverse-primary', colors.slate[950]),
            secondary: color('color-background-inverse-secondary', colors.slate[800]),
            tertiary: color('color-background-inverse-tertiary', colors.slate[600]),
          },

          /**
           * Legacy Tailwind theme
           */
          DEFAULT: color('background-color', '210deg 40% 96%'),
          highlight: color('background-highlight-color', variable('brand-50-color')),
          active: color('background-active-color', variable('brand-100-color')),
          disabled: color('background-disabled-color', '213deg 27% 84%'),
        },

        border: {
          /**
           * New Tailwind theme
           */
          primary: color('color-border-primary', variable('color-primary-600', '221deg 81% 56%')),
          hover: color('color-border-hover', variable('color-primary-400', '221deg 100% 72%')),
          neutral: color('color-border-neutral', colors.slate[400]),

          /**
           * Legacy Tailwind theme
           */
          DEFAULT: color('border-color', '213deg 27% 84%'),
          highlight: color('border-highlight-color', variable('brand-aa-color')),
          divider: color('border-divider-color', '212deg 27% 84%'),
          muted: color('border-divider-muted', '214deg 32% 91%'),
        },

        /**
         * Legacy Tailwind theme
         */
        /** @deprecated */
        brand: {
          DEFAULT: color('brand-color', variable('color-primary', '219deg 100% 59%')),
          aa: color('brand-aa-color', variable('color-primary-aa', '221deg 81% 56%')),
          50: color('brand-50-color', variable('color-primary-50', '214deg 100% 97%')),
          100: color('brand-100-color', variable('color-primary-100', '214deg 95% 93%')),
          200: color('brand-200-color', variable('color-primary-200', '213deg 97% 87%')),
          300: color('brand-300-color', variable('color-primary-300', '212deg 96% 78%')),
          400: color('brand-400-color', variable('color-primary-400', '213deg 94% 68%')),
          500: color('brand-500-color', variable('color-primary-500', '217deg 91% 60%')),
          600: color('brand-600-color', variable('color-primary-600', '219deg 100% 59%')),
          700: color('brand-700-color', variable('color-primary-700', '224deg 76% 48%')),
          800: color('brand-800-color', variable('color-primary-900', '226deg 71% 40%')),
          900: color('brand-900-color', variable('color-primary-900', '225deg 64% 33%')),
          950: color('brand-950-color', variable('color-primary-950', '226deg 57% 21%')),
        },
        /** @deprecated */
        text: {
          DEFAULT: color('text-color', '229deg 84% 5%'),
          muted: color('text-muted-color', '215deg 16% 47%'),
          highlight: color('text-highlight-color', variable('brand-aa-color')),
        },
        /** @deprecated */
        link: {
          DEFAULT: color('brand-link-color', '0deg 0% 0%'),
          hover: color('brand-link-hover-color', variable('brand-aa-color')),
        },
      }),
      animation: {
        loading: 'loading 1.25s ease-in-out infinite',
        'spin-slow': 'spin 2s linear infinite',
      },
      boxShadow: {
        focus: '0 1px 2px rgba(0, 0, 0, 0.05), 0 0 0 2px rgba(255, 255, 255, 1), 0 0 0 4px rgba(74, 134, 255, 1)',
        card: '0 4px 20px 0 rgba(0, 0, 0, 0.06)',
      },
      keyframes: {
        loading: {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' },
        },
      },
      zIndex: {
        dropdown: '100',
        backdrop: '120',
        tooltip: '130',
        modal: '150',
        lightbox: '200',
        toast: '9999',
      },
      borderRadius: {
        card: variable('radius-card', '8px'),
        action: variable('radius-action', '4px'),
      },
    },
  },
  plugins: [
    require('@tailwindcss/forms'),
    require('@headlessui/tailwindcss'),
    scrollbarHide,
    function ({ addVariant }: any) {
      addVariant(
        'safari',
        '@supports (hanging-punctuation: first) and (font: -apple-system-body) and (-webkit-appearance: none)',
      );
      addVariant('firefox', '@supports (-moz-appearance:none)');
      addVariant('whitelabel', ':global(:is(#LayoutWrapper.whitelabel, [data-modal-theme].whitelabel)) &');
    },
  ],
};

export default config;
